import {
	ChangeEvent,
	FunctionComponent,
	ReactNode,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import ReactDOM from "react-dom";

import { SearchField } from "@/components/SearchField/SearchField";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

interface InstructionMenuPopoverProps {
	/** The trigger button the menu anchors to. */
	anchorEl: HTMLElement | null;
	onClose: () => void;
	/** Search box state — both menus are searchable (PREC-3687 §4.1, AC 7 / AC 8). */
	searchValue: string;
	onSearchChange: (value: string) => void;
	searchPlaceholder: string;
	searchName: string;
	children: ReactNode;
	/**
	 * Portal target. The Chrome extension passes its shadow-root container so the menu renders
	 * inside the styled scope; on the web it defaults to `document.body` (web rule §11).
	 */
	portalContainer?: HTMLElement | null;
	/** Menu width in px — also drives the right-edge flip math. */
	width?: number;
}

/** Breathing room between the trigger and the menu, and between the menu and the viewport edge. */
const GAP = 6;
const VIEWPORT_MARGIN = 8;

/**
 * Below this the upward side is too cramped to be worth using and the menu flips down instead. Two
 * rows plus the search field — less than that and the list is a scroll sliver.
 */
const MIN_USABLE_HEIGHT = 180;

/** Where the menu ends up: anchored to the viewport bottom (upwards) or its top (downwards). */
interface MenuPosition {
	left: number;
	bottom?: number;
	top?: number;
	maxHeight: number;
}

/**
 * Which side of the trigger the menu opens on, and how tall it may be there. Pure so the arithmetic
 * is testable — jsdom reports an all-zero `getBoundingClientRect()`, so this is the only way any of
 * it gets asserted (`InstructionMenuPopover.spec.tsx`).
 */
const resolvePlacement = (
	rect: Pick<DOMRect, "top" | "bottom">,
	viewportHeight: number
): Omit<MenuPosition, "left"> => {
	const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
	const spaceBelow = viewportHeight - rect.bottom - GAP - VIEWPORT_MARGIN;

	// Upward is the preference, so it only loses when it is genuinely cramped *and* down is roomier.
	if (spaceAbove >= MIN_USABLE_HEIGHT || spaceAbove >= spaceBelow) {
		return { bottom: viewportHeight - rect.top + GAP, maxHeight: Math.max(0, spaceAbove) };
	}

	return { top: rect.bottom + GAP, maxHeight: Math.max(0, spaceBelow) };
};

/**
 * The searchable dropdown both instruction menus open (Variables and Templates).
 *
 * Portaled and `position: fixed` rather than absolutely positioned inside the editor box, because
 * the box, the section and the step body all clip their overflow — an in-flow menu would be cut
 * off at the textarea's bottom edge.
 *
 * It **prefers** to open upwards: the footer it anchors to sits at the bottom of the editor, so
 * downward would push the list off-screen on a short viewport. But upward is a preference, not a
 * rule — a trigger nearer the viewport top than the menu is tall gets flipped **down** instead,
 * because the alternative is a menu whose top (and therefore its search field) is off-screen. The
 * step body is its own scroll container, so scrolling the editor's footer up there is an ordinary
 * gesture. Either way the menu is given an explicit `maxHeight` for the side it landed on, so it
 * can never outgrow the space it was placed in.
 */
export const InstructionMenuPopover: FunctionComponent<InstructionMenuPopoverProps> = ({
	anchorEl,
	onClose,
	searchValue,
	onSearchChange,
	searchPlaceholder,
	searchName,
	children,
	portalContainer,
	width = 340,
}) => {
	const menuRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<MenuPosition | null>(null);

	useLayoutEffect(() => {
		if (!anchorEl) {
			return;
		}

		const updatePosition = () => {
			const rect = anchorEl.getBoundingClientRect();
			const left = Math.max(
				VIEWPORT_MARGIN,
				Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN)
			);

			setPosition({ ...resolvePlacement(rect, window.innerHeight), left });
		};

		updatePosition();

		// Capture phase: the step body is its own scroll container, so a bubbling listener on
		// `window` would never see it scroll and the menu would drift off its trigger.
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [anchorEl, width]);

	useEffect(() => {
		const handlePointerDown = (event: MouseEvent) => {
			// Resolve through shadow retargeting: in the extension panel this listener sits outside
			// the shadow root, where `event.target` is the shadow host — every inside click would
			// read as "outside" and close the menu before the option's click landed.
			const target = getComposedEventTarget(event);

			if (target && !menuRef.current?.contains(target) && !anchorEl?.contains(target)) {
				onClose();
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [anchorEl, onClose]);

	if (!position) {
		return null;
	}

	return ReactDOM.createPortal(
		<div
			ref={menuRef}
			// `flex flex-col` + a `maxHeight` on the box and `flex-1` on the list: whichever side the
			// menu landed on, the search field stays visible and only the options scroll.
			className="fixed z-[2000] flex flex-col overflow-hidden rounded-xl border border-border-light bg-white shadow-lg"
			data-testid={`${searchName}-menu`}
			role="menu"
			style={{
				bottom: position.bottom,
				top: position.top,
				left: position.left,
				maxHeight: position.maxHeight,
				width,
			}}
		>
			<div className="shrink-0 border-b border-border-light p-2">
				<SearchField
					handleChange={(event: ChangeEvent<HTMLInputElement>) =>
						onSearchChange(event.target.value)
					}
					name={searchName}
					placeholder={searchPlaceholder}
					value={searchValue}
				/>
			</div>
			<div className="max-h-[260px] flex-1 overflow-y-auto bb-scrollbar-darker py-1">
				{children}
			</div>
		</div>,
		portalContainer ?? document.body
	);
};
